import { useState, useEffect } from 'react';
import { PhoneOff, Mic, MicOff, Hash } from 'lucide-react';
import { useVoice } from '@/context/VoiceContext';
import api from '@/lib/api';

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

function formatDuration(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function getInitials(name = '') {
  return name.split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?';
}

export default function ActiveCallPanel() {
  const {
    callStatus, callerInfo, callDuration,
    isMuted, toggleMute, hangUp, sendDigits, incomingCall,
    acceptIncoming, rejectIncoming,
  } = useVoice();

  const [showDtmf, setShowDtmf] = useState(false);
  const [recordingOn, setRecordingOn] = useState(false);
  const isOnCall = callStatus !== 'idle';

  useEffect(() => {
    api.get('/voice/recording-settings')
      .then(r => setRecordingOn(!!r.data?.enabled))
      .catch(() => {});
  }, []);

  // --- Incoming call state ---
  if (incomingCall) {
    const from = callerInfo?.name || callerInfo?.number || 'Unknown';
    return (
      <div
        data-testid="active-call-incoming"
        className="
          flex flex-col items-center justify-center h-full
          bg-gradient-to-b from-[#0F172A] to-[#1E293B] rounded-2xl
          px-8 py-12 space-y-8 relative overflow-hidden
        "
      >
        {/* Ripple animation */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="absolute rounded-full border border-blue-400/20 animate-ping"
              style={{
                width: `${120 + i * 60}px`,
                height: `${120 + i * 60}px`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: '1.5s',
              }}
            />
          ))}
        </div>

        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-blue-500/30 border-2 border-blue-400/50 flex items-center justify-center">
            <span className="text-2xl font-bold text-white">{getInitials(from)}</span>
          </div>
          <div className="text-center">
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-widest mb-1">Incoming Call</p>
            <p data-testid="active-call-name" className="text-xl font-semibold text-white">{from}</p>
            {callerInfo?.number && callerInfo?.name !== callerInfo?.number && (
              <p className="text-sm text-slate-400 font-mono mt-1">{callerInfo.number}</p>
            )}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-6">
          <button
            data-testid="active-call-reject-btn"
            onClick={rejectIncoming}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-xl transition-all active:scale-95"
          >
            <PhoneOff className="w-7 h-7 text-white" />
          </button>
          <button
            data-testid="active-call-accept-btn"
            onClick={acceptIncoming}
            className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center shadow-xl transition-all active:scale-95"
          >
            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.5 19.79 19.79 0 012 2.06 2 2 0 014 .07h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // --- Active call state ---
  if (isOnCall) {
    const name = callerInfo?.name || callerInfo?.number || 'Unknown';
    return (
      <div
        data-testid="active-call-panel"
        className="
          flex flex-col items-center justify-between h-full
          bg-gradient-to-b from-[#0F172A] via-[#0F172A] to-[#162032]
          rounded-2xl px-6 py-8 relative overflow-hidden
        "
      >
        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />

        {/* Top: Contact info */}
        <div className="relative z-10 flex flex-col items-center gap-4 w-full">
          <div className={`
            w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white
            ${callStatus === 'in-progress' ? 'bg-emerald-500/30 border-2 border-emerald-400/40' : 'bg-slate-700/50 border-2 border-slate-600/40'}
          `}>
            {getInitials(name)}
          </div>
          <div className="text-center">
            <p data-testid="active-call-name" className="text-lg font-semibold text-white">{name}</p>
            {callerInfo?.number && callerInfo?.name !== callerInfo?.number && (
              <p className="text-sm text-slate-400 font-mono">{callerInfo.number}</p>
            )}
          </div>

          {/* Status & Timer */}
          <div className="flex items-center gap-2">
            <span className={`
              flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full
              ${callStatus === 'in-progress' ? 'bg-emerald-500/20 text-emerald-300' :
                callStatus === 'ringing' ? 'bg-amber-500/20 text-amber-300' :
                'bg-blue-500/20 text-blue-300'
              }
            `}>
              <span className={`w-2 h-2 rounded-full animate-pulse ${
                callStatus === 'in-progress' ? 'bg-emerald-400' :
                callStatus === 'ringing' ? 'bg-amber-400' : 'bg-blue-400'
              }`} />
              {callStatus === 'in-progress' && formatDuration(callDuration)}
              {callStatus === 'ringing' && 'Ringing...'}
              {callStatus === 'connecting' && 'Connecting...'}
            </span>
          </div>

          {recordingOn && (
            <span
              data-testid="active-call-recording-indicator"
              className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-red-500/20 text-red-300 border border-red-400/30"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" /> REC · This call is being recorded
            </span>
          )}
        </div>

        {/* DTMF Keypad overlay */}
        {showDtmf && (
          <div className="relative z-10 w-full">
            <div className="grid grid-cols-3 gap-2 mt-2">
              {DTMF_KEYS.map(k => (
                <button
                  key={k}
                  data-testid={`dtmf-key-${k}`}
                  onClick={() => sendDigits(k)}
                  className="
                    h-11 rounded-xl bg-white/10 hover:bg-white/20
                    text-white font-semibold text-lg
                    active:scale-95 transition-all duration-100
                  "
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bottom: Controls */}
        <div className="relative z-10 flex items-center gap-4 w-full justify-center">
          {/* Mute */}
          <button
            data-testid="active-call-mute-btn"
            onClick={toggleMute}
            className={`
              flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all w-16
              ${isMuted ? 'bg-amber-500/30 text-amber-300' : 'bg-white/10 text-slate-300 hover:bg-white/20'}
            `}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            <span className="text-[10px] font-medium">{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {/* Hang up */}
          <button
            data-testid="active-call-hangup-btn"
            onClick={hangUp}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-2xl shadow-red-900/40 transition-all active:scale-95"
          >
            <PhoneOff className="w-6 h-6 text-white" />
          </button>

          {/* Keypad toggle */}
          <button
            data-testid="active-call-dtmf-btn"
            onClick={() => setShowDtmf(s => !s)}
            className={`
              flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all w-16
              ${showDtmf ? 'bg-blue-500/30 text-blue-300' : 'bg-white/10 text-slate-300 hover:bg-white/20'}
            `}
          >
            <Hash className="w-5 h-5" />
            <span className="text-[10px] font-medium">Keypad</span>
          </button>
        </div>
      </div>
    );
  }

  // --- Idle placeholder ---
  return (
    <div
      data-testid="active-call-idle"
      className="
        flex flex-col items-center justify-center h-full
        bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200
        text-slate-400
      "
    >
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-3">
        <svg className="w-8 h-8 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.5 19.79 19.79 0 012 2.06 2 2 0 014 .07h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
        </svg>
      </div>
      <p className="text-sm font-medium text-slate-500">No active call</p>
      <p className="text-xs text-slate-400 mt-1">Dial a number or select a contact</p>
    </div>
  );
}
